import {
    addressesThunk,
    initEvent,
    organizationThunk,
    retentionPoliciesThunk,
    startLogoutListener,
    userSettingsThunk,
    userThunk,
    welcomeFlagsActions,
} from '@proton/account';
import * as bootstrap from '@proton/account/bootstrap';
import { bootstrapEvent } from '@proton/account/bootstrap/action';
import { serverEvent } from '@proton/account/eventLoop';
import { getDecryptedPersistedState } from '@proton/account/persist/helper';
import { createCalendarModelEventManager } from '@proton/calendar/calendarModelEventManager';
import { setupGuestCrossStorage } from '@proton/cross-storage/account/guest';
import { FeatureCode, fetchFeatures } from '@proton/features';
import { logger } from '@proton/logger';
import { ALL_CONSOLE_LEVELS } from '@proton/logger/constants';
import type { LogLevel } from '@proton/logger/types';
import { contactEmailsThunk } from '@proton/mail/store/contactEmails';
import { categoriesThunk } from '@proton/mail/store/labels';
import { mailSettingsThunk } from '@proton/mail/store/mailSettings';
import createApi from '@proton/shared/lib/api/createApi';
import { getEvents } from '@proton/shared/lib/api/events';
import { getSilentApi } from '@proton/shared/lib/api/helpers/customConfig';
import { generateLoggerKey } from '@proton/shared/lib/authentication/loggerKey';
import { registerSessionRemovalListener } from '@proton/shared/lib/authentication/persistedSessionStorage';
import { loadAllowedTimeZones } from '@proton/shared/lib/date/timezone';
import { isChromiumBased } from '@proton/shared/lib/helpers/browser';
import { isElectronMail } from '@proton/shared/lib/helpers/desktop';
import { initSafariFontFixClassnames } from '@proton/shared/lib/helpers/initSafariFontFixClassnames';
import { isProduction } from '@proton/shared/lib/helpers/sentry';
import type { ProtonConfig } from '@proton/shared/lib/interfaces';
import { startMailForegroundTelemetry } from '@proton/shared/lib/telemetry/mailForegroundTelemetry';
import { appMode } from '@proton/shared/lib/webpack.constants';
import { CommonFeatureFlag } from '@proton/unleash/Flags';
import noop from '@proton/utils/noop';

import { cleanDataLogout } from './helpers/cleanData';
import { cleanLegacyLogsDatabase } from './helpers/cleanLegacyLogsDatabase';
import {
    canLoadRunner,
    shouldLoadMigrationWorker,
} from './helpers/encryptedSearch/migration-system/helpers/shouldLoadMigrationWorker';
import { migrationToolWorker } from './helpers/encryptedSearch/migration-system/migrationToolWorker';
import { registerMailToProtocolHandler } from './helpers/url';
import locales from './locales';
import { type MailState, extendStore, setupStore } from './store/store';

const getAppContainer = () =>
    import(/* webpackChunkName: "MainContainer" */ './MainContainer').then((result) => result.default);

export const bootstrapApp = async ({ config }: { config: ProtonConfig }) => {
    const appName = config.APP_NAME;
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const api = createApi({ config });
    const silentApi = getSilentApi(api);
    const authentication = bootstrap.createAuthentication();
    bootstrap.init({ config, authentication, locales });
    setupGuestCrossStorage({ appMode, appName });
    initSafariFontFixClassnames();
    startLogoutListener();
    // If the browser is Chromium based, register automatically the mailto protocol handler
    if (isChromiumBased()) {
        registerMailToProtocolHandler();
    }

    if (isElectronMail) {
        void import('@proton/shared/lib/desktop/bootstrapMailInboxDesktop').then((module) => {
            // Also registers baseline IPC and redirect protocols for the inbox desktop app.
            module.bootstrapMailInboxDesktop({
                config,
                authentication,
                api,
            });
        });
    }

    const run = async () => {
        const appContainerPromise = getAppContainer();
        const sessionResult = await bootstrap.loadSession({ authentication, api, pathname, searchParams });

        const history = bootstrap.createHistory({ sessionResult, pathname });
        const unleashClient = bootstrap.createUnleash({ api: silentApi });
        const unleashPromise = bootstrap
            .unleashReady({ unleashClient })
            .then(() => {
                // If the WebApiRateLimiter flag is enabled, configure the rate limiter with the payload value
                if (unleashClient.isEnabled(CommonFeatureFlag.WebApiRateLimiter)) {
                    const config = unleashClient.getVariant(CommonFeatureFlag.WebApiRateLimiter).payload?.value;

                    if (config) {
                        const configParsed = JSON.parse(config);
                        api.apiRateLimiter.configure({
                            maxRequests: configParsed.maxRequests,
                            windowMs: configParsed.windowMs,
                        });
                    }

                    api.apiRateLimiter.enable();
                }
            })
            .catch(noop);

        const user = sessionResult.session?.User;
        extendStore({ config, api, authentication, unleashClient, history });

        const persistedState = await getDecryptedPersistedState<Partial<MailState>>({
            authentication,
            user,
        });

        const store = setupStore({ preloadedState: persistedState?.state });
        const dispatch = store.dispatch;

        if (user) {
            dispatch(initEvent({ User: user }));
        }

        const loadUser = async () => {
            const [user, userSettings, features] = await Promise.all([
                dispatch(userThunk()),
                dispatch(userSettingsThunk()),
                dispatch(
                    fetchFeatures([
                        FeatureCode.EarlyAccessScope,
                        FeatureCode.MailActionsChunkSize,
                        FeatureCode.AccountSecurityDismissed2FACard,
                        FeatureCode.CategoryViewBeta,
                    ])
                ),
            ]);

            dispatch(welcomeFlagsActions.initial(userSettings));

            bootstrap.enableTelemetryBasedOnUserSettings({ userSettings });
            await bootstrap.loadLocales({ userSettings, locales });

            return { user, userSettings, earlyAccessScope: features[FeatureCode.EarlyAccessScope] };
        };

        const loadPreload = () => {
            return Promise.all([
                dispatch(categoriesThunk()),
                dispatch(mailSettingsThunk()),
                dispatch(organizationThunk()),
                dispatch(addressesThunk()),
                dispatch(contactEmailsThunk()),
            ]);
        };

        const loadPreloadButIgnored = () => {
            loadAllowedTimeZones(silentApi).catch(noop);
            dispatch(retentionPoliciesThunk()).catch(noop);
        };

        const preloadPromise = loadPreload();
        const userPromise = loadUser();
        loadPreloadButIgnored();

        const [MainContainer, userData] = await Promise.all([
            appContainerPromise,
            userPromise,
            bootstrap.loadCrypto({ appName, unleashClient }),
            unleashPromise,
        ]);

        // Get rid of any remaining legacy database that was created during alpha.
        cleanLegacyLogsDatabase().catch(noop);

        // Initialize logger if the feature flag is enabled
        if (unleashClient.isEnabled('CollectLogs')) {
            const { key: loggerKey, ID: loggerID } = await generateLoggerKey(authentication);

            const consoleLevels: LogLevel[] | undefined = isProduction(window.location.host)
                ? undefined
                : ALL_CONSOLE_LEVELS;
            void logger.initialize({
                encryptionKey: loggerKey,
                appName,
                loggerID,
                loggerName: 'mail',
                consoleLevels,
            });

            api.addEventListener((event) => {
                if (event.type === 'api-error') {
                    // Ping event can be noisy as they are triggered every 5000ms while offline
                    const isPing = event.payload.apiInfo.url === 'tests/ping';
                    if (!isPing) {
                        logger.error(event.payload.apiInfo.url || 'unknown URL', event.payload);
                    }
                }

                // Passive observer for all events, return false so we never claim to handle any of them.
                return false;
            });
        }
        // postLoad needs everything to be loaded.
        await bootstrap.postLoad({ appName, authentication, ...userData, history });
        // Preloaded models are not needed until the app starts, and also important do it postLoad as these requests might fail due to missing scopes.
        await preloadPromise;

        startMailForegroundTelemetry(silentApi);

        const isEncryptedSearchMigrationSystemDisabled = unleashClient.isEnabled(
            'EncryptedSearchMigrationSystemDisabled'
        );
        if (!isEncryptedSearchMigrationSystemDisabled) {
            const shouldLoad = await shouldLoadMigrationWorker(userData.user);
            const canLoad = await canLoadRunner();

            if (shouldLoad && canLoad) {
                void migrationToolWorker({
                    user: userData.user,
                    keyPassword: authentication.getPassword(),
                    metricsEnabled: !!userData.userSettings.Telemetry,
                    api,
                });
            }
        }

        const eventManager = bootstrap.eventManager({
            api: silentApi,
            query: (eventID) => {
                const state = store.getState();
                const orgSettings = state.organization.value?.Settings.MailCategoryViewEnabled;
                const mailSettings = state.mailSettings.value?.MailCategoryView;

                return getEvents(eventID, {
                    ConversationCounts: 1,
                    MessageCounts: 1,
                    OnlyInInboxForCategoriesCounts: orgSettings && mailSettings ? 1 : 0,
                });
            },
        });
        const calendarModelEventManager = createCalendarModelEventManager({ api: silentApi });

        extendStore({ eventManager, calendarModelEventManager });

        eventManager.subscribe((event) => {
            dispatch(serverEvent(event));
        });
        eventManager.start();

        dispatch(bootstrapEvent({ type: 'complete' }));

        registerSessionRemovalListener(cleanDataLogout);

        return {
            ...userData,
            eventManager,
            unleashClient,
            history,
            store,
            MainContainer,
        };
    };

    return bootstrap.wrap({ appName, authentication }, run());
};
